import { RouterStore } from '@superwf/mobx-react-router';

import { MobXProviderContext } from 'mobx-react';
import React from 'react';
import { InitialState } from '../../typings/initial';
import AnalyticsService from '../services/AnalyticsService';
import ApiService from '../services/ApiService';
import { RouteService } from '../services/RouteService';
import { SsoClientService } from '../services/SsoClientService';

import SalesFrontendRepository from '../services/SalesFrontendRepository';
import AddressStore from './AddressStore';
import CookieConsentStore from './CookieConsentStore';
import DebtNoticeStore, { IDebtNoticeStore } from './DebtNoticeStore';
import { DirectPaymentStore } from './DirectPaymentStore';
import { FeatureStore } from './FeatureStore';
import { default as InvoiceDetailsStore } from './InvoiceDetailsStore';
import InvoiceFileStore from './InvoiceFileStore';
import { IInvoicePaymentModalStore, InvoicePaymentModalStore } from './InvoicePaymentModalStore';
import { InvoicePaymentStore } from './InvoicePaymentStore';
import InvoiceSettingsModalStore from './InvoiceSettingsModalStore';
import InvoiceSettingsStore from './InvoiceSettingsStore';
import InvoiceSettingsViewStore from './view/InvoiceSettingsViewStore';
import InvoicesStore, { IInvoicesStore } from './InvoicesStore';
import LanguageStore, { ILanguageStore } from './LanguageStore';
import LayoutStore from './LayoutStore';
import { default as MandatesStore, IMandatesStore } from './MandatesStore';
import { IModalStore, ModalStore } from './ModalStore';
import PaymentDetailsStore, { IPaymentDetailsStore } from './PaymentDetailsStore';
import PaymentHistoryStore, { IPaymentHistoryStore } from './PaymentHistoryStore';
import PosPaymentStore, { IPosPaymentStore } from './PosPaymentStore';
import { default as PreviousInvoicesFilterStore, IPreviousInvoicesFilterStore } from './PreviousInvoicesFilterStore';
import ReferenceNumberStore from './ReferenceNumberStore';
import RouteStore, { IRouteStore } from './RouteStore';
import { SsoService } from './SsoService';
import { ISsoStore, SsoStore } from './SsoStore';
import { default as TransactionStore, ITransactionStore } from './TransactionStore';
import TranslateStore, { ITranslateStore } from './TranslateStore';
import InvoiceSettingsPanelEmailValidationViewStore from './view/InvoiceSettingsPanelEmailValidationViewStore';
import InvoiceSettingsPanelStore from './InvoiceSettingsPanelStore';
import InvoiceNameStore from './InvoiceNameStore';
import EInvoiceSuppliersDataStore from './data/EInvoiceSuppliersDataStore';
import InvoiceSettingsChangeLogStore from './InvoiceSettingsChangeLogStore';
import CurrentBalancesStore from './CurrentBalancesStore';
import ProductCurrentBalanceModalStore from './ProductCurrentBalanceModalStore';
import UnsettledReceivablesStore from './UnsettledReceivablesStore';
import PreviousInvoicesStore from './PreviousInvoicesStore';
import InvoiceDetailsTableViewStore from './view/InvoiceDetailsTableViewStore';
import InvoicePriceStore from './InvoicePriceStore';
import InvoiceDetailsContentViewStore from './view/InvoiceDetailsContentViewStore';
import InvoiceLineUsagesDataStore from './data/InvoiceLineUsagesDataStore';
import InvoiceLineUsagesTableViewStore from './view/InvoiceLineUsagesTableViewStore';
import EmployeeUsagesStore from './EmployeeUsagesStore';
import InvoiceDetailsTabsStore from './InvoiceDetailsTabsStore';
import InvoiceDetailsFooterViewStore from './view/InvoiceDetailsFooterViewStore';
import InvoiceDeliveryLogDataStore from './data/InvoiceDeliveryLogDataStore';
import NonPeriodicInvoicesSyncStore from './NonPeriodicInvoicesSyncStore';

export default class RootStore {
  public cookieConsentStore: CookieConsentStore;
  public ssoClientService: SsoClientService;
  public ssoStore: ISsoStore;
  public ssoService: SsoService;
  public routing: RouterStore;
  public routeStore: IRouteStore;
  public languageStore: ILanguageStore;
  public invoicesStore: IInvoicesStore;
  public invoiceFileStore: InvoiceFileStore;
  public invoiceDetailsStore: InvoiceDetailsStore;
  public invoiceDetailsTableViewStore: InvoiceDetailsTableViewStore;
  public invoiceDetailsFooterViewStore: InvoiceDetailsFooterViewStore;
  public paymentHistoryStore: IPaymentHistoryStore;
  public translateStore: ITranslateStore;
  public initialState: InitialState;
  public previousInvoicesFilterStore: IPreviousInvoicesFilterStore;
  public mandatesStore: IMandatesStore;
  public invoicePaymentModalStore: IInvoicePaymentModalStore;
  public invoiceSettingsStore: InvoiceSettingsStore;
  public currentBalancesStore: CurrentBalancesStore;
  public productCurrentBalanceModalStore: ProductCurrentBalanceModalStore;
  public invoiceSettingsViewStore: InvoiceSettingsViewStore;
  public invoiceSettingsChangeLogStore: InvoiceSettingsChangeLogStore;
  public invoiceSettingsModalStore: InvoiceSettingsModalStore;
  public addressStore: AddressStore;
  public modalStore: IModalStore;
  public unsettledReceivablesStore: UnsettledReceivablesStore;
  public previousInvoicesStore: PreviousInvoicesStore;
  public invoicePriceStore: InvoicePriceStore;
  public invoiceDetailsContentViewStore: InvoiceDetailsContentViewStore;
  public invoiceLineUsagesDataStore: InvoiceLineUsagesDataStore;
  public invoiceLineUsagesTableViewStore: InvoiceLineUsagesTableViewStore;
  public employeeUsagesStore: EmployeeUsagesStore;
  public invoiceDetailsTabsStore: InvoiceDetailsTabsStore;
  public invoiceSettingsPanelEmailValidationViewStore: InvoiceSettingsPanelEmailValidationViewStore;
  public eInvoiceSuppliersDataStore: EInvoiceSuppliersDataStore;
  public invoiceSettingsPanelStore: InvoiceSettingsPanelStore;
  public invoiceNameStore: InvoiceNameStore;
  public transactionStore: ITransactionStore;
  public debtNoticeStore: IDebtNoticeStore;
  public posPaymentStore: IPosPaymentStore;
  public featureStore: FeatureStore;
  public nonPeriodicInvoicesSyncStore: NonPeriodicInvoicesSyncStore;

  public salesFrontendRepository: SalesFrontendRepository;
  public apiService: ApiService;
  public analyticsService: AnalyticsService;
  public invoicePaymentStore: InvoicePaymentStore;
  public routeService: RouteService;
  public directPaymentStore: DirectPaymentStore;
  public referenceNumberStore: ReferenceNumberStore;
  public paymentDetailsStore: IPaymentDetailsStore;
  public layoutStore: LayoutStore;
  public invoiceDeliveryLogDataStore: InvoiceDeliveryLogDataStore;

  constructor(routing: RouterStore, initialState: InitialState) {
    this.layoutStore = new LayoutStore();
    this.layoutStore.init();
    this.initialState = initialState;
    this.routing = routing;
    this.cookieConsentStore = new CookieConsentStore();
    this.ssoClientService = new SsoClientService();
    this.ssoService = new SsoService(this.ssoClientService);
    this.apiService = new ApiService(this.ssoService);

    this.modalStore = new ModalStore();
    this.invoicePriceStore = new InvoicePriceStore();
    this.routeStore = new RouteStore(routing);
    this.languageStore = new LanguageStore(this.routeStore);
    this.translateStore = new TranslateStore(this.languageStore, this.apiService);
    this.translateStore.init();
    this.ssoStore = new SsoStore(this.languageStore, this.ssoClientService, this.ssoService, this.routeStore, this.modalStore);
    this.ssoStore.init();
    this.mandatesStore = new MandatesStore(this.apiService, this.ssoStore);
    this.invoicesStore = new InvoicesStore(this.ssoStore, this.apiService, this.translateStore);
    this.previousInvoicesFilterStore = new PreviousInvoicesFilterStore(this.invoicesStore);
    this.previousInvoicesStore = new PreviousInvoicesStore(this.ssoStore, this.languageStore, this.apiService, this.previousInvoicesFilterStore);
    this.employeeUsagesStore = new EmployeeUsagesStore(this.ssoStore);
    this.salesFrontendRepository = new SalesFrontendRepository(this.ssoStore);

    this.analyticsService = new AnalyticsService(this.ssoStore);

    this.transactionStore = new TransactionStore(this.apiService);
    this.unsettledReceivablesStore = new UnsettledReceivablesStore(this.ssoStore, this.apiService, this.invoicesStore);
    this.invoiceDetailsStore = new InvoiceDetailsStore(
      this.apiService,
      this.translateStore,
      this.routeStore,
      this.modalStore,
      this.unsettledReceivablesStore,
      this.previousInvoicesStore,
      this.layoutStore
    );
    this.invoiceFileStore = new InvoiceFileStore(this.ssoStore, this.apiService, this.employeeUsagesStore, this.invoiceDetailsStore, this.routeStore);
    this.invoiceDetailsTabsStore = new InvoiceDetailsTabsStore(this.invoiceFileStore, this.invoiceDetailsStore, this.translateStore, this.mandatesStore);
    this.referenceNumberStore = new ReferenceNumberStore(this.apiService, this.ssoStore, this.translateStore);
    this.invoiceDetailsFooterViewStore = new InvoiceDetailsFooterViewStore(this.invoiceDetailsStore, this.unsettledReceivablesStore);
    this.invoiceDetailsTableViewStore = new InvoiceDetailsTableViewStore(this.invoiceDetailsStore, this.layoutStore, this.modalStore);
    this.invoiceDetailsContentViewStore = new InvoiceDetailsContentViewStore(this.invoiceDetailsStore);
    this.invoiceLineUsagesDataStore = new InvoiceLineUsagesDataStore(this.apiService);
    this.invoiceLineUsagesTableViewStore = new InvoiceLineUsagesTableViewStore(this.invoiceDetailsStore);
    this.invoiceSettingsStore = new InvoiceSettingsStore(this.apiService, this.ssoStore);
    this.invoiceSettingsChangeLogStore = new InvoiceSettingsChangeLogStore(this.apiService, this.ssoStore);
    this.invoiceSettingsPanelEmailValidationViewStore = new InvoiceSettingsPanelEmailValidationViewStore(this.apiService);
    this.eInvoiceSuppliersDataStore = new EInvoiceSuppliersDataStore(this.apiService);
    this.invoiceNameStore = new InvoiceNameStore(this.ssoStore, this.translateStore);
    this.invoiceSettingsPanelStore = new InvoiceSettingsPanelStore(
      this.apiService,
      this.invoiceNameStore,
      this.ssoStore,
      this.eInvoiceSuppliersDataStore,
      this.invoiceSettingsPanelEmailValidationViewStore
    );
    this.invoiceSettingsModalStore = new InvoiceSettingsModalStore(
      this.apiService,
      this.ssoStore,
      this.invoiceSettingsPanelStore,
      this.invoiceSettingsStore,
      this.eInvoiceSuppliersDataStore,
      this.modalStore,
      this.analyticsService
    );
    this.invoiceSettingsViewStore = new InvoiceSettingsViewStore(
      this.translateStore,
      this.invoiceSettingsModalStore,
      this.eInvoiceSuppliersDataStore,
      this.invoiceSettingsChangeLogStore,
      this.invoiceSettingsPanelStore
    );
    this.addressStore = new AddressStore(this.apiService);
    this.paymentHistoryStore = new PaymentHistoryStore(this.ssoStore, this.transactionStore, this.translateStore, this.apiService, this.mandatesStore);
    this.paymentDetailsStore = new PaymentDetailsStore(this.apiService, this.ssoStore);
    this.routeService = new RouteService(this.routeStore);
    this.invoicePaymentModalStore = new InvoicePaymentModalStore(this.unsettledReceivablesStore, this.modalStore, this.routeService);
    this.debtNoticeStore = new DebtNoticeStore(this.apiService, this.ssoStore);
    this.posPaymentStore = new PosPaymentStore(this.apiService, this.routeStore, this.modalStore, this.ssoStore);
    this.currentBalancesStore = new CurrentBalancesStore(this.apiService, this.ssoStore, this.languageStore);
    this.productCurrentBalanceModalStore = new ProductCurrentBalanceModalStore(this.salesFrontendRepository, this.modalStore);
    this.featureStore = new FeatureStore(this.routeStore);
    this.invoicePaymentStore = new InvoicePaymentStore(
      this.unsettledReceivablesStore,
      this.invoicePaymentModalStore,
      this.posPaymentStore,
      this.ssoStore,
      this.modalStore
    );
    this.directPaymentStore = new DirectPaymentStore(this.routeStore, this.languageStore, this.apiService, this.routeService);
    this.invoiceDeliveryLogDataStore = new InvoiceDeliveryLogDataStore(this.apiService, this.ssoStore);
    this.nonPeriodicInvoicesSyncStore = new NonPeriodicInvoicesSyncStore(this.apiService, this.ssoStore);
  }
}

export function useStores(): RootStore {
  return React.useContext(MobXProviderContext) as RootStore;
}
